/**
 * В модуле cm собран код инициализации основных разделов сайта.
 * Этот модуль должен импортироваться в каждую точку входа,
 * которая помимо инициализации основного функционала страницы
 * должна активировать поиск, работу меню, корзины, авторизации и т. д.
 * В особых случаях можно не импортировать cm целиком, а вызвать
 * в точке входа только необходимые для работы данного раздела модули.
 */

import globals, { ability } from 'Js/common/globals';
import { getCookie, getLocalData } from 'Js/lib/localdata';
import { isStandardClickPeriod } from 'Js/lib/isStandardClickPeriod';
import {
    runAfterPaint,
    runWhenIdle,
    runSequence
} from 'Js/lib/runtimeScheduler';

// Отключаем предупреждения о якобы некорректном использовании import()
// https://github.com/typescript-eslint/typescript-eslint/issues/2108

// Импорт core-js оптимизируется через Babel настройкой useBuiltIns
// https://github.com/zloirock/core-js#babelpreset-env

// Синхронно загружаемые модули
import 'core-js/stable';
import 'Js/common/service-workers';
import 'Js/common/polyfills';
import 'Js/common/pro-analytics';

// Разделы, в которых нужно показать модал с предупреждением о цене клика
const CLICK_PRICE_MODAL_PATHS = ['warehouses', 'shops'];

// Приоритетные UI-модули
runAfterPaint(() => {
    runSequence([
        !getCookie('privacyCookieSettings')
            ? () => import('Js/common/privacy-cookie')
            : null,
        () => import('Js/common/search'),
        () => import('Js/common/nav'),
        () => import('Js/common/helper')
    ]);
});

if (globals.user.isGuest) {
    runAfterPaint(() => {
        runSequence([
            () => import('Js/common/auth'),
            () => import('Js/common/social-login')
        ]);
    });
} else {
    runAfterPaint(() => {
        runSequence([
            () => import('Js/common/left-block'),
            () => import('Js/common/update-pm-counts'),
            () => import('Js/common/logout-bpanel-user')
        ]);
    });
}

// Низкоприоритетные UI-модули
runAfterPaint(() => {
    runSequence([
        () => import('Js/common/cart-summary'),
        () => import('Js/common/scroll-top-btn'),
        () => import('Js/common/language-select')
    ]);
});

if (!globals.user.isGuest && !window.location.href.includes('/sorry')) {
    runAfterPaint(() => {
        runSequence([
            () => import('Js/common/orders'),
            () => import('Js/common/notifications')
        ]);
    });
}

// Дополнительный SEO-функционал
if (ability.can('manage', 'SEO')) {
    runAfterPaint(() => {
        import('Js/common/seo-block');
    });
}

// Админский функционал "войти как пользователь"
if (ability.can('manage', 'User') || globals.isEmulatingUser) {
    runAfterPaint(() => {
        import('Js/common/switch-user');
    });
}

// Показувати модал відгуку в Google тільки на іспанській та російській версії сайту
if (
    globals.featureFlags.get('IsAvtoproEs') ||
    globals.featureFlags.get('IsAvtopro')
) {
    runAfterPaint(() => {
        import('Js/common/add-review-modal');
    });
}

if (
    globals.user.isSeller &&
    CLICK_PRICE_MODAL_PATHS.includes(
        window.location.pathname.replaceAll('/', '')
    ) &&
    isStandardClickPeriod()
) {
    runAfterPaint(() => {
        import('Js/common/click-price-modal');
    });
}

if (globals.user.isSeller) {
    runAfterPaint(() => {
        import('Js/common/boost-modal');
    });
}

if (
    globals.user.isSeller &&
    JSON.parse(getLocalData('welcome_seller') || 'false')
) {
    runAfterPaint(() => {
        import('Js/common/welcome-seller-modal');
    });
}

// Прогресс по уровням скидок для покупателя
if (!globals.user.isSeller && !globals.user.isGuest) {
    runAfterPaint(() => {
        import('Js/common/loyalty-steps-chart');
    });
}

// Добавление данных о ветке
if (window.location.href.includes('vus.us')) {
    runWhenIdle(() => {
        import('Js/common/build-version');
    });
}

runWhenIdle(() => {
    import('Js/common/web-vitals');
});

// Виртуальный модуль (см. webpack.config.cjs),
// содержащий в себе импорты ресурсных файлов на всех языках,
// в режиме разработки это позволяет реагировать на изменения в них
if (__DEVSERVER__ || __WATCHMODE__) {
    // eslint-disable-next-line import/no-unresolved, import/no-useless-path-segments
    import('.virtual/resx-version');
}
